import { useTranslation } from "react-i18next";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAlerts } from "context/AlertContext";
import {
  IGetNotificationsForUser,
  INotification,
} from "models/notificationItem.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import {
  NOTIFICATIONS_CURRENT_USER_ID,
  QueryAPIKey,
} from "utils/constants/api.constants";

export default function useMarkNotificationRead() {
  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();
  const queryClient = useQueryClient();

  const {
    status,
    error,
    isPending,
    isError,
    isSuccess,
    data: notificationResponse,
    mutate: triggerMarkNotificationAsRead,
  } = useMutation({
    mutationFn: ({
      id,
      notification,
    }: {
      id: string;
      notification: INotification;
    }) =>
      services.notifications
        .markNotificationRead(id, notification)
        .then((res) => res.data),
    onMutate: async (notificationResponse) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [
          QueryAPIKey.GetNotificationsForUser,
          NOTIFICATIONS_CURRENT_USER_ID,
        ],
      });

      // Grab current query data state
      const previousNotifications = queryClient.getQueryData<
        InfiniteData<AxiosResponse<IGetNotificationsForUser>>
      >([QueryAPIKey.GetNotificationsForUser, NOTIFICATIONS_CURRENT_USER_ID]);

      // Set query data with updated notification read state
      queryClient.setQueryData<
        InfiniteData<AxiosResponse<IGetNotificationsForUser>>
      >(
        [QueryAPIKey.GetNotificationsForUser, NOTIFICATIONS_CURRENT_USER_ID],
        (prevQueryData) => {
          if (!prevQueryData) {
            return;
          }

          const modifiedPages = prevQueryData.pages.map((page) => {
            const updatedPageContent = page.data.content.map(
              (notification: INotification) => {
                return notification.id === notificationResponse.id
                  ? { ...notification, ...notificationResponse.notification }
                  : notification;
              },
            );

            return {
              ...page,
              data: { ...page.data, content: updatedPageContent },
            };
          });

          return {
            ...prevQueryData,
            pages: modifiedPages,
          };
        },
      );

      return { previousNotifications };
    },
    onError: (err, notificationResponse, context) => {
      showAlert({
        severity: AlertSeverity.Error,
        message: t("markNotificationAsReadFailure"),
      });

      queryClient.setQueryData<
        InfiniteData<AxiosResponse<IGetNotificationsForUser>>
      >(
        [QueryAPIKey.GetNotificationsForUser, NOTIFICATIONS_CURRENT_USER_ID],
        context?.previousNotifications,
      );
    },
    onSuccess: () => {
      showAlert({
        severity: AlertSeverity.Success,
        message: t("markNotificationAsReadSuccess"),
      });
    },
  });

  return {
    status,
    error,
    isPending,
    isError,
    isSuccess,
    notificationResponse,
    triggerMarkNotificationAsRead,
  };
}
